import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	FormControl,
	Grid,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from "@mui/material"
import { addDays, format } from "date-fns"
import { DateRangePicker, defaultStaticRanges } from "react-date-range"
import * as locales from "react-date-range/dist/locale"
import "react-date-range/dist/styles.css" // main css file
import "react-date-range/dist/theme/default.css"

import { AxiosPromise } from "axios"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { AppDispatch } from "../../store"
import { getFilenames } from "../../store/actions/file"
import ReportDialog from "../report/dialog"
import BankAutoComplete from "./bank-autocomplete"
import CampaignAutoComplete from "./campaign-autocomplete"
import ChannelPlickMultiSelect from "./channel-plick-multi-select"
import CorporateAutoComplete from "./corporate-autocomplete"
import StatusPlickMultiSelect from "./status-plick-multi-select"

interface IDatePicker {
	startDate: undefined | Date
	endDate: undefined | Date
	key: string
}

interface IFiltersReportProps {
	filterBank: string | undefined
	setFilterBank: (bank: string | undefined) => void
	filterCorporate: string | undefined
	setFilterCorporate: (corporate: string | undefined) => void
	filterCampaign: string | undefined
	setFilterCampaign: (campaign: string | undefined) => void
	filterFilename: string | undefined
	setFilterFilename: (filename: string | undefined) => void
	filterStatus: string[]
	setFilterStatus: (statusList: string[]) => void
	filterChannel: string[]
	setFilterChannel: (channelList: string[]) => void
	datePicker: IDatePicker[]
	setDatePicker: (value: IDatePicker[]) => void
	openDialog: boolean
	setOpenDialog: (value: boolean) => void
	handleGenerate: () => void
}

function translateRange(dictionary) {
	return (item) =>
		dictionary[item.label] ? { ...item, label: dictionary[item.label] } : item
}

const ReportFilter = ({
	filterBank,
	setFilterBank,
	filterCampaign,
	setFilterCampaign,
	filterCorporate,
	setFilterCorporate,
	filterFilename,
	setFilterFilename,
	filterChannel,
	setFilterChannel,
	filterStatus,
	setFilterStatus,
	datePicker,
	handleGenerate,
	setDatePicker,
	openDialog,
	setOpenDialog,
}: IFiltersReportProps) => {
	const { t } = useTranslation()
	const dispatch = useDispatch<AppDispatch>()
	const [openDatePicker, setOpenDatePicker] = useState<boolean>(false)
	const [filenames, setFilenames] = useState<string[]>([])

	const handleChangeFileName = (event: SelectChangeEvent) => {
		setFilterFilename(event.target.value as string)
	}

	useEffect(() => {
		if (!filterBank) return
		dispatch(
			getFilenames({
				bankId: filterBank,
				corporateId: filterCorporate,
				campaignLogo: filterCampaign,
			})
		)
			.then((response: AxiosPromise) => {
				setFilenames(response.payload)
			})
			.catch(() => {})
	}, [filterBank, filterCampaign, filterCorporate])

	const staticRangesLabels = {
		Today: "Oggi",
		Yesterday: "Ieri",
		"This Week": "Questa settimana",
		"Last Week": "Settimana scorsa",
		"This Month": "Questo mese",
		"Last Month": "Mese scorso",
	}

	const inputRangesLabels = {
		"days up to today": "giorni prima di oggi",
		"days starting today": "giorni da oggi",
	}

	const ruStaticRanges = defaultStaticRanges.map(
		translateRange(staticRangesLabels)
	)
	// const ruInputRanges = defaultInputRanges.map(translateRange(inputRangesLabels));

	const handleClickToggleDatePicker = () => {
		/*if (openDatePicker) {
			handleGenerate()
		}*/

		setOpenDatePicker(!openDatePicker)
	}

	const localesSelected =
		window.localStorage.getItem("i18nextLng") === "en"
			? "enGB"
			: (window.localStorage.getItem("i18nextLng") ?? "it")

	const resetFilter = () => {
		setDatePicker([
			{
				startDate: undefined,
				endDate: undefined,
				key: "selection",
			},
		])
		setFilterCampaign(undefined)
		setFilterCorporate(undefined)
		setFilterFilename(undefined)
		setFilenames([])
	}

	return (
		<Box sx={{ backgroundColor: "#FFF", padding: "16px" }}>
			<h1
				style={{
					backgroundColor: "#FFF",
					fontSize: "18px",
					marginBottom: "32px",
					color: "#1C334C",
					fontWeight: 600,
				}}
			>
				{t("title", { ns: "report" })}
				<div id="loginSubtitleSpan">{t("description", { ns: "report" })}</div>
			</h1>

			<h2
				style={{
					backgroundColor: "#FFF",
					fontSize: "18px",
					marginBottom: "32px",
					color: "#1C334C",
					fontWeight: 600,
				}}
			>
				{t("filter.title", { ns: "report" })}
				<div id="loginSubtitleSpan">
					{t("filter.description", { ns: "report" })}
				</div>
			</h2>
			<Grid container>
				<Grid item xs={12} sm={12} md={6} sx={{ padding: "16px" }}>
					<BankAutoComplete
						disableClearable={true}
						filterBank={filterBank}
						setFilterBank={setFilterBank}
					/>
				</Grid>
				<Grid item xs={12} sm={12} md={6} sx={{ padding: "16px" }}>
					<CorporateAutoComplete
						filterCorporate={filterCorporate}
						setFilterCorporate={setFilterCorporate}
					/>
				</Grid>
				<Grid item xs={12} sm={12} md={6} sx={{ padding: "16px" }}>
					<CampaignAutoComplete
						filterCorporate={filterCorporate}
						filterCampaign={filterCampaign}
						setFilterCampaign={setFilterCampaign}
					/>
				</Grid>
				<Grid item xs={12} sm={12} md={6} sx={{ padding: "16px" }}>
					<StatusPlickMultiSelect
						statusSelected={filterStatus}
						setStatusSelected={setFilterStatus}
					/>
				</Grid>
				<Grid item xs={12} sm={12} md={6} sx={{ padding: "16px" }}>
					<ChannelPlickMultiSelect
						channelSelected={filterChannel}
						setChannelSelected={setFilterChannel}
					/>
				</Grid>
				<Grid item xs={12} sm={12} md={6} sx={{ padding: "16px" }}>
					<TextField
						InputLabelProps={{ shrink: true }}
						id="dateRange"
						label={t("date", { ns: "common" })}
						placeholder="dd/mm/yyyy | dd/mm/yyyy"
						value={
							datePicker?.[0]?.startDate
								? `${format(datePicker?.[0]?.startDate, "dd/MM/yyyy")} - ${format(datePicker?.[0]?.endDate, "dd/MM/yyyy")}`
								: ""
						}
						inputProps={{ readOnly: true }}
						InputProps={{
							endAdornment: (
								<InputAdornment position="start">
									<CalendarTodayIcon />
								</InputAdornment>
							),
						}}
						sx={{ width: "100%" }}
						variant="standard"
						onClick={handleClickToggleDatePicker}
					/>
					<Dialog
						sx={{ maxWidth: "60vw" }}
						open={openDatePicker}
						onClose={handleClickToggleDatePicker}
					>
						<DialogContent>
							<DateRangePicker
								color="red"
								onChange={(item) => setDatePicker([item.selection])}
								// showSelectionPreview={true}
								dateDisplayFormat={"dd/MM/yyyy"}
								moveRangeOnFirstSelection={false}
								minDate={addDays(new Date(), -(365 * 4))}
								maxDate={new Date()}
								months={2}
								ranges={datePicker}
								direction="horizontal"
								locale={locales[localesSelected][localesSelected]}
								staticRanges={ruStaticRanges}
								inputRanges={[]}
								preventSnapRefocus={true}
								calendarFocus="backwards"
							/>
						</DialogContent>
						<DialogActions>
							<Grid container justifyContent={"space-between"}>
								<Button onClick={handleClickToggleDatePicker}>OK</Button>
							</Grid>
						</DialogActions>
					</Dialog>
				</Grid>
				<Grid item xs={12} sm={12} md={12} sx={{ padding: "16px" }}>
					<FormControl variant="standard" sx={{ m: 1, minWidth: "100%" }}>
						<InputLabel id="filename-label">
							{t("filter.filename", { ns: "report" })}
						</InputLabel>
						<Select
							labelId="filename-label"
							value={filterFilename}
							onChange={handleChangeFileName}
							placeholder={t("filer.fileNameplaceholder", { ns: "common" })}
						>
							{filenames.map((item, index) => (
								<MenuItem key={"filename_" + index} value={item}>
									{item}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
			</Grid>
			<Grid
				container
				justifyContent={"space-between"}
				alignItems={"center"}
				sx={{ backgroundColor: "#F1F1F1", padding: "0 16px" }}
			>
				<Grid item sx={{ width: "calc(50% - 32px)", padding: "16px 0" }}>
					<Button
						sx={{ width: "calc(100% - 16px)", marginLeft: "16px" }}
						variant="outlined"
						onClick={resetFilter}
					>
						{t("filter.buttons.cancel", { ns: "report" })}
					</Button>
				</Grid>
				<Grid item sx={{ width: "calc(50% - 32px)", padding: "16px 0" }}>
					<Button
						variant="contained"
						onClick={handleGenerate}
						style={{ width: "calc(100% - 32px)", margin: "16px" }}
					>
						{t("filter.buttons.send", { ns: "report" })}
					</Button>
				</Grid>
			</Grid>
			<ReportDialog openDialog={openDialog} setOpenDialog={setOpenDialog} />
		</Box>
	)
}

export default ReportFilter
